import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import ModalBox from './modal-box'

import styles from './dialog.module.css'

const propTypes = {
    aria: PropTypes.shape({
        describedby: PropTypes.string,
        labelledby: PropTypes.string
    }),
    ariaHideApp: PropTypes.bool,
    bodyOpenClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    closeTimeoutMS: PropTypes.number,
    contentRef: PropTypes.func,
    htmlOpenClassName: PropTypes.string,
    icon: PropTypes.element,
    isOpen: PropTypes.bool,
    maxWidth: PropTypes.bool,
    message: PropTypes.node.isRequired,
    minWidth: PropTypes.bool,
    onAfterOpen: PropTypes.func,
    onRequestClose: PropTypes.func,
    overlayClassName: PropTypes.string,
    overlayRef: PropTypes.func,
    parentSelector: PropTypes.func,
    portalClassName: PropTypes.string,
    role: PropTypes.string,
    shouldCloseOnEsc: PropTypes.bool,
    shouldCloseOnOverlayClick: PropTypes.bool,
    shouldFocusAfterRender: PropTypes.bool,
    shouldReturnFocusAfterClose: PropTypes.bool,
    style: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        content: PropTypes.objectOf(PropTypes.any),
        // eslint-disable-next-line react/forbid-prop-types
        overlay: PropTypes.objectOf(PropTypes.any)
    }),
    textClassName: PropTypes.string,
    title: PropTypes.string
}

const defaultProps = {
    isOpen: true,
    maxWidth: true,
    minWidth: true
}

/**
 * Convenient wrapper around the ModalBox to ease setting content.
 *
 * @see ModalBox
 * @see {@link https://github.com/reactjs/react-modal}
 *
 * @example <caption>Basic usage</caption>
 * <code>
 *     <Dialog isOpen={true} message={'Are you really really sure?'}>
 *          <nav>
 *              <button>Yes</button>
 *              <button>No</button>
 *          </nav>
 *     </Dialog>
 * </code>
 *
 * @param {Object} props - The current component properties
 * @param {Object} [props.aria] - Directly passed to ModalBox
 * @param {boolean} [props.ariaHideApp] - Directly passed to ModalBox
 * @param {string} [props.bodyOpenClassName] - Directly passed to ModalBox
 * @param {ReactElement} [props.children] - The component childs
 * @param {string} [props.className] - Merging optional classname for wrapper
 * @param {number} [props.closeTimeoutMS] - Directly passed to ModalBox
 * @param {Function} [props.contentRef] - Directly passed to ModalBox
 * @param {string} [props.htmlOpenClassName] - Directly passed to ModalBox
 * @param {ReactElement} [props.icon] - The single icon component to be displayed
 * @param {booelan} [props.isOpen=false] - Whether the modal is visible or not
 * @param {boolean} [props.maxWidth=true] - Whether the width is limitted or not
 * @param {ReactElement} props.message - The text message to be displayed
 * @param {boolean} [props.minWidth=true] - Whether the content has a minimial width
 * @param {Function} [props.onAfterOpen] -  Directly passed to ModalBox
 * @param {Function} [props.onRequestClose] -  Directly passed to ModalBox
 * @param {string} [props.overlayClassName] - Directly passed to ModalBox
 * @param {Function} [props.overlayRef] - Directly passed to ModalBox
 * @param {Function} [props.parentSelector] - Directly passed to ModalBox
 * @param {string} [props.portalClassName] - Directly passed to ModalBox
 * @param {string} [props.role] - Directly passed to ModalBox
 * @param {boolean} [props.shouldCloseOnEsc] - Directly passed to ModalBox
 * @param {boolean} [props.shouldCloseOnOverlayClick] - Directly passed to ModalBox
 * @param {boolean} [props.shouldFocusAfterRender] - Directly passed to ModalBox
 * @param {boolean} [props.shouldReturnFocusAfterClose] - Directly passed to ModalBox
 * @param {Object} [props.style] - Directly passed to ModalBox
 * @param {string} [props.textClassName] - Merging optional classname for text
 * @param {string} [props.title] - The title to be displayed, also used for contentLabel
 * @returns {ReactElement} The modal markup
 */
function Dialog(props) {
    const {
        aria,
        ariaHideApp,
        bodyOpenClassName,
        children,
        className,
        closeTimeoutMS,
        contentRef,
        htmlOpenClassName,
        icon,
        isOpen,
        maxWidth,
        message,
        minWidth,
        onAfterOpen,
        onRequestClose,
        overlayClassName,
        overlayRef,
        parentSelector,
        portalClassName,
        role,
        shouldCloseOnEsc,
        shouldCloseOnOverlayClick,
        shouldFocusAfterRender,
        shouldReturnFocusAfterClose,
        style,
        textClassName,
        title
    } = props

    const composedClassName = cx(
        {
            [styles['max-width']]: maxWidth,
            [styles['min-width']]: minWidth
        },
        className,
        't3sel-modal-dialog'
    )
    const composedContentClassname = cx(styles['content'], 't3sel-modal-dialog-content')
    const composedIconClassName = cx(
        styles['icon'],
        {
            [styles['hidden']]: !icon
        },
        't3sel-modal-dialog-icon'
    )
    const composedTextClassName = cx(styles['text'], textClassName, 't3sel-modal-dialog-text')
    const composedTitleClassName = cx(
        styles['title'],
        {
            [styles['hidden']]: !title
        },
        't3sel-modal-dialog-title'
    )
    const composedMessageClassName = cx(styles['message'], 't3sel-modal-dialog-message')

    return (
        <ModalBox
            isOpen={isOpen}
            className={composedClassName}
            contentLabel={title}
            {...{
                aria,
                ariaHideApp,
                bodyOpenClassName,
                closeTimeoutMS,
                contentRef,
                htmlOpenClassName,
                onAfterOpen,
                onRequestClose,
                overlayClassName,
                overlayRef,
                parentSelector,
                portalClassName,
                role,
                shouldCloseOnEsc,
                shouldCloseOnOverlayClick,
                shouldFocusAfterRender,
                shouldReturnFocusAfterClose,
                style
            }}
        >
            <div className={composedContentClassname}>
                {icon && (
                    <div className={composedIconClassName} aria-hidden>
                        {icon}
                    </div>
                )}
                <article className={composedTextClassName}>
                    <h2 className={composedTitleClassName}>{title}</h2>
                    {/**
                     * Use div and not p even if it looks semantically wrong, because
                     * message could (and will be) other html elements containing p's
                     * which will produce errors then...
                     */}
                    <div className={composedMessageClassName}>{message}</div>
                </article>
            </div>
            {children}
        </ModalBox>
    )
}

Dialog.propTypes = propTypes
Dialog.defaultProps = defaultProps

export default Dialog
